/**
 * useJsonLd — imperatively injects a Schema.org JSON-LD <script> tag into <head>.
 *
 * Why: react-helmet-async v2 silently strips/ignores <script> children content,
 * so any <script type="application/ld+json"> placed inside <Helmet> never reaches
 * the rendered DOM. Title/meta/link still work via Helmet — only scripts are affected.
 *
 * This hook bypasses Helmet for scripts only. It:
 *   1. Builds a <script type="application/ld+json"> element with the given id
 *   2. Appends it to document.head on mount/data change
 *   3. Cleans up on unmount
 *
 * The id ensures multiple pages don't collide and that React StrictMode's
 * double-mount doesn't create duplicate scripts.
 */

import { useEffect } from 'react';

export function JsonLd({ id, data }) {
  useJsonLd(id, data);
  return null;
}

export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;

    const elementId = `jsonld-${id}`;
    // Remove any existing tag with the same id (handles re-renders & StrictMode double-mount)
    const existing = document.getElementById(elementId);
    if (existing) existing.parentNode.removeChild(existing);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = elementId;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      const node = document.getElementById(elementId);
      if (node && node.parentNode) node.parentNode.removeChild(node);
    };
  }, [id, JSON.stringify(data)]);
}
